import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post, get, session, ApiError } from '../lib/api';

interface Store { code: string; nameEn: string }

/**
 * PIN login. Deliberately a big on-screen keypad: tills are often touchscreens,
 * and a cashier changing shift should be able to log in without a keyboard.
 */
export default function Login() {
  const nav = useNavigate();
  const [pin, setPin] = useState('');
  const [storeCode, setStoreCode] = useState(localStorage.getItem('femi9.store') ?? 'BH21101');
  const [terminalCode, setTerminalCode] = useState(localStorage.getItem('femi9.terminal') ?? '01');
  const [stores, setStores] = useState<Store[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Unauthenticated, so this may fail — the hardcoded fallback keeps the
    // login screen usable even if the API is unreachable.
    get<Store[]>('/api/v1/stores').then(setStores).catch(() => setStores([
      { code: 'BH21101', nameEn: 'City Centre Bahrain' },
      { code: 'BH21103', nameEn: 'The Avenues' },
      { code: 'BH21102', nameEn: 'Warehouse Bahrain' },
    ]));
  }, []);

  async function submit(value: string) {
    if (value.length < 4) { setError('PIN must be at least 4 digits'); return; }
    setBusy(true); setError('');
    try {
      const data = await post<any>('/api/v1/auth/login', { pin: value, storeCode, terminalCode });
      session.set({ token: data.token, user: data.user, storeCode, terminalCode });
      localStorage.setItem('femi9.store', storeCode);
      localStorage.setItem('femi9.terminal', terminalCode);
      nav('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  function tap(d: string) {
    if (d === 'C') { setPin(''); setError(''); return; }
    if (d === '<') { setPin((p) => p.slice(0, -1)); return; }
    const next = (pin + d).slice(0, 8);
    setPin(next);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) tap(e.key);
      else if (e.key === 'Backspace') tap('<');
      else if (e.key === 'Enter') void submit(pin);
      else if (e.key === 'Escape') tap('C');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pin, storeCode, terminalCode]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      <div className="card p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-3xl font-bold text-brand tracking-tight">FEMI9</div>
          <div className="text-sm text-gray-500 mt-1">Point of Sale · Bahrain</div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="label">Store</label>
            <select className="input w-full" value={storeCode} onChange={(e) => setStoreCode(e.target.value)}>
              {stores.map((s) => <option key={s.code} value={s.code}>{s.nameEn}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Till</label>
            <select className="input w-full" value={terminalCode} onChange={(e) => setTerminalCode(e.target.value)}>
              {['01', '02', '03'].map((t) => <option key={t} value={t}>Till {t}</option>)}
            </select>
          </div>
        </div>

        <label className="label">Staff PIN</label>
        <div className="h-14 mb-3 rounded-lg bg-gray-950 border border-gray-700 flex items-center justify-center gap-3">
          {pin.length === 0
            ? <span className="text-gray-600 text-sm">Enter your PIN</span>
            : Array.from(pin).map((_, i) => <span key={i} className="w-3 h-3 rounded-full bg-brand" />)}
        </div>

        {error && (
          <div className="mb-3 text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '<'].map((d) => (
            <button
              key={d}
              onClick={() => tap(d)}
              className={`h-14 rounded-lg text-xl font-semibold ${
                d === 'C' || d === '<' ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-800 hover:bg-gray-700'
              }`}
            >
              {d === '<' ? '⌫' : d}
            </button>
          ))}
        </div>

        <button
          className="btn-primary w-full mt-4 h-12 text-base"
          disabled={busy || pin.length < 4}
          onClick={() => void submit(pin)}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="mt-5 pt-4 border-t border-gray-800 text-[11px] text-gray-500 leading-relaxed">
          <div className="font-medium text-gray-400 mb-1">Demo PINs</div>
          274859 cashier · 639482 supervisor · 583927 store manager · 918273 admin
        </div>
      </div>
    </div>
  );
}
